import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectEnvironmentInteractionPoints } from "../../app/slices/EnvironmentSlice";
import {
  closeSequencePositionPicker,
  selectSequencePositionPicker,
  selectSelectedTab,
  SelectedTab,
} from "../../app/slices/GlobalSlice";
import {
  selectSelectedElement,
  selectSelectedElementType,
  selectActors,
  selectSelectedTake3D,
} from "../../app/slices/TrainingSlice";
import useSituationHelper from "../../hooks/SituationHelper.hook";
import { ISequence } from "../../models/interactions/Sequence";
import { ISendActorToPosition } from "../../models/interactions/SendActorToPosition";
import { InteractionType } from "../../models/interactions/Interaction";
import useUpdateSequenceInteraction from "../sequence/node_previews/UpdateSequenceInteraction.hook";
import InteractionPointPreview, {
  ActorPointStrategy,
  NpcPointStrategy,
  PlayerPointStrategy,
} from "./InteractionPointPreview";
import classNames from "classnames";
import { sequenceUncertainClassName, sequenceStateDot, sequenceStateDotClassName } from "./sequenceStateDot";
import { Actor } from "../../models/Actor";
import { useSequenceActorPlacements } from "./useSequenceStateOnMap";
import { RgbaColor } from "../../app/slices/EnvironmentSlice";
import {
  getEnvironmentPositionContexts,
  getSituationPositionId,
} from "../sequence/EnvironmentPositionContext";


export default function Viewport3DSequenceActorPositionLayer() {
  const dispatch = useDispatch();
  const currentTab = useSelector(selectSelectedTab);
  const picker = useSelector(selectSequencePositionPicker);
  const selectedElementType = useSelector(selectSelectedElementType);
  const sequence = useSelector(selectSelectedElement) as ISequence | undefined;
  const currentTake = useSelector(selectSelectedTake3D);
  const actors = useSelector(selectActors);
  const takePositions = useSelector(selectEnvironmentInteractionPoints) ?? [];
  const { getSituationMetadata } = useSituationHelper();
  const { updateInteraction } = useUpdateSequenceInteraction();
  // Where the sequence has the actors by now, rather than where the take starts them.
  const placements = useSequenceActorPlacements();

  const activeInteraction = useMemo(() => {
    return sequence?.nodes?.find((node) => node.id === picker.nodeId)?.data
      ?.interaction as ISendActorToPosition | undefined;
  }, [picker.nodeId, sequence?.nodes]);

  const isSequenceContext =
    selectedElementType === "takeSequence" ||
    (selectedElementType === "interaction" &&
      sequence?.type === InteractionType.Sequence);

  const isVisible =
    currentTab === SelectedTab.Timeline &&
    isSequenceContext &&
    picker.isOpen &&
    picker.positionType === "actor";

  if (!isVisible || !activeInteraction) {
    return null;
  }

  const actorByPositionId = new Map(
    (currentTake?.actors ?? [])
      .map((actorState) => {
        const actor = actors.find(
          (candidate) => candidate.id === actorState.actorId,
        );
        if (!actor) return undefined;
        return [actorState.positionId, actor] as const;
      })
      .filter((entry): entry is readonly [string, Actor] => !!entry),
  );

  const allRenderedActorPositions = getEnvironmentPositionContexts(
    takePositions,
    currentTake?.situations,
    getSituationMetadata,
  ).flatMap((context) =>
    context.actorPositions.map((actorPosition) => ({
      actorPosition,
      positionId: getSituationPositionId(context, actorPosition.id),
      takeColor: context.takeColor,
    })),
  );

  const getStrategyForActorPosition = (actor?: Actor) => {
    if (!actor) return NpcPointStrategy;
    if (actor.id === "player") return PlayerPointStrategy;
    return ActorPointStrategy;
  };

  const getBackgroundColor = (
    actor: Actor | undefined,
    takeColor?: RgbaColor,
  ) => {
    if (actor?.id === "player") return takeColor;
    return undefined;
  };

  return (
    <>
      {allRenderedActorPositions.map(
        ({ actorPosition, positionId, takeColor }) => {
          const placement = placements
            ? placements.get(positionId) ?? placements.get(actorPosition.id)
            : undefined;
          const actor = placements
            ? actors.find((candidate) => candidate.id === placement?.actorId)
            : actorByPositionId.get(positionId);
          const isSelected =
            activeInteraction.positionId === positionId ||
            activeInteraction.positionId === actorPosition.id;

          return (
            <InteractionPointPreview
              key={positionId}
              strategy={getStrategyForActorPosition(actor)}
              x={actorPosition.position.x}
              y={actorPosition.position.y}
              actor={actor}
              markerBorderColor={takeColor}
              markerBackgroundColor={getBackgroundColor(actor, takeColor)}
              directionDegrees={actorPosition.direction?.x}
              directionColor={takeColor}
              iconClassName={classNames(
                {
                  "sequence-target-rim": isSelected,
                  "sequence-under-attachment": placement?.holdsProp,
                },
                sequenceUncertainClassName(placement),
              )}
              badgeContent={sequenceStateDot(placement)}
              badgeClassName={sequenceStateDotClassName(placement)}
              onClick={() => {
                if (
                  activeInteraction.type !== InteractionType.SendActorToPosition
                ) {
                  return;
                }

                updateInteraction(picker.nodeId, {
                  ...activeInteraction,
                  positionId,
                } as ISendActorToPosition);
                dispatch(closeSequencePositionPicker());
              }}
            />
          );
        },
      )}
    </>
  );
}
